import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Divider from "@mui/material/Divider";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RestoreIcon from "@mui/icons-material/Restore";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import EditNoteIcon from "@mui/icons-material/EditNote";
import GenericAlertModal from "../../../components/modals/GenericAlertModal";
import {
	useStartTreatmentConfigChatTurn,
	useTreatmentConfigChatSession,
} from "../../../hooks/useTreatmentPlans";
import {
	TreatmentConfigOptions,
	TreatmentConfigTemplate,
} from "../../../domain/treatmentPlan.interface";
import {
	ChatMessageBubble,
	chatStatusColor,
	chatStatusLabel,
} from "./chat/ChatMessageBubble";
import { useSpeechToText } from "./chat/useSpeechToText";
import { useScrollToLatest } from "./chat/useScrollToLatest";
import { AgentConfigDraft } from "../agent/draftToTemplate";
import { applyDraft } from "../agent/applyDraft";
import { normaliseDays, ProposedOptions } from "../agent/draftToOptions";

/** Id → display name, so the summary can be read rather than decoded. */
export interface DraftLabels {
	behaviors: Record<string, string>;
	engines: Record<string, string>;
}

interface TreatmentConfigChatProps {
	treatmentPlanId: string;
	/** The plan's option lists — what the agent picks from and adds to. */
	planOptions: TreatmentConfigOptions | undefined;
	labels: DraftLabels;
	/**
	 * Hand the plan both halves at once. The template references options the
	 * merge may have just created, so applying one without the other would
	 * produce a template pointing at a schedule the plan does not have.
	 */
	onApply: (application: {
		options: TreatmentConfigOptions;
		template: TreatmentConfigTemplate;
	}) => void;
	/**
	 * Options the assistant has settled on, handed over as it goes rather than
	 * at the end — the merge only adds, so the form below can offer them while
	 * the conversation is still running.
	 */
	onOptionsProposed: (proposed: ProposedOptions) => void;
	/** Leave the conversation and finish the captured half by hand. */
	onContinueManually: (draft: AgentConfigDraft | null) => void;
}

const sessionStorageKey = (treatmentPlanId: string): string =>
	`sims-treatment-config-chat-session:${treatmentPlanId}`;

/**
 * A row of the draft summary. Reads as a sentence rather than as JSON — the
 * clinician is checking a config, not debugging a payload.
 */
const DraftRow: React.FC<{ label: string; value: React.ReactNode }> = ({
	label,
	value,
}) => (
	<Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
		<Typography variant="body2" color="text.secondary">
			{label}
		</Typography>
		<Typography variant="body2" sx={{ textAlign: "right", ml: 2 }}>
			{value}
		</Typography>
	</Stack>
);

const DraftSummary: React.FC<{
	draft: AgentConfigDraft | null;
	planOptions: TreatmentConfigOptions | undefined;
	labels: DraftLabels;
}> = ({ draft, planOptions, labels }) => {
	if (!draft) {
		return (
			<Typography variant="body2" color="text.secondary">
				The assistant will build the config here as you describe it.
			</Typography>
		);
	}

	// An ObjectId tells the reader nothing about whether the right thing was
	// chosen, which is the only question this panel exists to answer.
	const behaviorName =
		labels.behaviors[draft.behavior ?? ""] ?? draft.behavior ?? "";
	const engineName =
		labels.engines[draft.rewardEngineId ?? ""] ?? draft.rewardEngineId ?? "";
	// Resolved against the *merged* options, so a schedule the assistant
	// proposed this turn reads as chosen rather than as "not on this plan".
	const chosen = String(draft.cmVisitScheduleOptionIds ?? "");
	const chosenDays = normaliseDays(chosen);
	const schedule = (planOptions?.cmVisitScheduleOptions ?? []).find(
		(option) =>
			String(option.cmVisitScheduleOptions) === chosen ||
			(!!chosenDays &&
				normaliseDays(option.cmVisitScheduleOptionsDays) === chosenDays),
	);

	return (
		<Box>
			{draft.name && <DraftRow label="Name" value={draft.name} />}
			{draft.duration !== undefined && (
				<DraftRow label="Duration" value={`${draft.duration} weeks`} />
			)}
			{draft.visitsPerPeriod !== undefined && (
				<DraftRow label="Visits per period" value={draft.visitsPerPeriod} />
			)}
			{draft.minHoursBetweenVisits !== undefined && (
				<DraftRow
					label="Min hours between visits"
					value={draft.minHoursBetweenVisits}
				/>
			)}
			{draft.behavior && <DraftRow label="Behaviour" value={behaviorName} />}
			{draft.rewardEngineId && (
				<DraftRow label="Reward engine" value={engineName} />
			)}
			{/* Named by its days, not its number — the number means nothing to a reader. */}
			<DraftRow
				label="Visit schedule"
				value={
					schedule
						? schedule.cmVisitScheduleOptionsDaysDisplay ||
							schedule.cmVisitScheduleOptionsDays
						: draft.cmVisitScheduleOptionIds
							? `Option ${draft.cmVisitScheduleOptionIds} (not on this plan)`
							: "Not chosen"
				}
			/>
			{Array.isArray(draft.segments) && draft.segments.length > 0 && (
				<DraftRow label="Segments" value={`${draft.segments.length} phases`} />
			)}
		</Box>
	);
};

const TreatmentConfigChat: React.FC<TreatmentConfigChatProps> = ({
	treatmentPlanId,
	planOptions,
	labels,
	onApply,
	onOptionsProposed,
	onContinueManually,
}) => {
	// Restore the sessionId so closing and reopening the plan resumes the design.
	const [sessionId, setSessionId] = useState<string | null>(() => {
		try {
			return localStorage.getItem(sessionStorageKey(treatmentPlanId));
		} catch {
			return null;
		}
	});
	const [composer, setComposer] = useState("");
	const [showStartOverConfirm, setShowStartOverConfirm] = useState(false);
	const [applied, setApplied] = useState(false);

	const [didResumeFromStorage] = useState<boolean>(() => {
		try {
			return !!localStorage.getItem(sessionStorageKey(treatmentPlanId));
		} catch {
			return false;
		}
	});
	const [resumeHintDismissed, setResumeHintDismissed] = useState(false);

	const appendFinalSpeech = useCallback((finalText: string) => {
		const trimmed = finalText.trim();
		if (!trimmed) return;
		setComposer((prev) => {
			if (!prev) return trimmed;
			return prev.endsWith(" ") ? prev + trimmed : prev + " " + trimmed;
		});
	}, []);

	const [pendingAutoSend, setPendingAutoSend] = useState(false);

	const speech = useSpeechToText({
		onFinal: appendFinalSpeech,
		silenceMs: 15_000,
		stopWordPattern: /\bsend\s+message\b/i,
		onStopWord: () => setPendingAutoSend(true),
	});

	const startTurnMutation = useStartTreatmentConfigChatTurn();
	const sessionQuery = useTreatmentConfigChatSession(
		treatmentPlanId,
		sessionId,
		!!sessionId,
	);

	const session = sessionQuery.data ?? null;
	const draft = (session?.draft ?? null) as AgentConfigDraft | null;

	// A restored sessionId that no longer resolves is wiped rather than left to
	// fail on every poll.
	useEffect(() => {
		if (sessionQuery.isError && sessionId) {
			try {
				localStorage.removeItem(sessionStorageKey(treatmentPlanId));
			} catch {
				// ignore
			}
			setSessionId(null);
		}
	}, [sessionQuery.isError, sessionId, treatmentPlanId]);

	const transcriptRef = useScrollToLatest([
		session?.messages.length,
		session?.status,
	]);

	// A new draft supersedes whatever was applied before.
	useEffect(() => {
		setApplied(false);
	}, [draft]);

	/**
	 * Hand proposed options to the plan as they arrive.
	 *
	 * Runs on every draft rather than on a button, which is only safe because
	 * the merge adds and never removes, and because the form drops a merge that
	 * changes nothing — so this settles instead of looping.
	 */
	useEffect(() => {
		if (draft?.options) onOptionsProposed(draft.options);
	}, [draft?.options, onOptionsProposed]);

	const isTurnInFlight =
		startTurnMutation.isPending ||
		(session?.status === "IN_PROGRESS" && !sessionQuery.isError);

	const handleSend = useCallback(() => {
		const text = composer.trim();
		if (!text) return;

		startTurnMutation.mutate(
			{
				id: treatmentPlanId,
				payload: { sessionId: sessionId ?? undefined, message: text },
			},
			{
				onSuccess: (data) => {
					if (!sessionId) {
						setSessionId(data.sessionId);
						try {
							localStorage.setItem(
								sessionStorageKey(treatmentPlanId),
								data.sessionId,
							);
						} catch {
							// ignore — storage may be unavailable
						}
					} else {
						// Polling was paused while awaiting input; force it to resume.
						sessionQuery.refetch();
					}
					setComposer("");
					setResumeHintDismissed(true);
				},
			},
		);
	}, [composer, sessionId, startTurnMutation, sessionQuery, treatmentPlanId]);

	useEffect(() => {
		if (!pendingAutoSend) return;
		setPendingAutoSend(false);
		if (composer.trim() && !isTurnInFlight) {
			handleSend();
		}
	}, [pendingAutoSend, composer, isTurnInFlight, handleSend]);

	const conversion = useMemo(
		() => applyDraft(draft, planOptions),
		[draft, planOptions],
	);

	const confirmStartOver = (): void => {
		try {
			localStorage.removeItem(sessionStorageKey(treatmentPlanId));
		} catch {
			// ignore
		}
		setSessionId(null);
		setComposer("");
		setResumeHintDismissed(true);
		setApplied(false);
		startTurnMutation.reset();
		setShowStartOverConfirm(false);
	};

	const startTurnError = useMemo(() => {
		if (!startTurnMutation.isError) return null;
		const err = startTurnMutation.error as { errorMessage?: string } | Error;
		if (
			err &&
			typeof err === "object" &&
			"errorMessage" in err &&
			err.errorMessage
		) {
			return err.errorMessage;
		}
		return (err as Error)?.message ?? "Failed to send message.";
	}, [startTurnMutation.isError, startTurnMutation.error]);

	return (
		<Box>
			<Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
				<Box sx={{ flex: "1 1 60%", minWidth: 320 }}>
					<Paper
						variant="outlined"
						sx={{ p: 2, display: "flex", flexDirection: "column", height: 520 }}
					>
						{didResumeFromStorage &&
							!resumeHintDismissed &&
							session &&
							session.messages.length > 0 && (
								<Alert
									severity="info"
									icon={<RestoreIcon fontSize="small" />}
									sx={{ mb: 1 }}
									onClose={() => setResumeHintDismissed(true)}
								>
									Resumed your earlier design session.
								</Alert>
							)}

						<Box
							ref={transcriptRef}
							sx={{ flex: 1, overflowY: "auto", pr: 1, mb: 1 }}
						>
							{!session && !startTurnMutation.isPending && (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ textAlign: "center", mt: 6 }}
								>
									Describe the program in plain English — how long it runs, how
									often participants attend, what is measured and how it pays.
									The assistant asks for anything missing and checks the config
									as it goes.
								</Typography>
							)}

							{session?.messages.map((m, idx) => (
								<ChatMessageBubble key={idx} msg={m} />
							))}

							{isTurnInFlight && (
								<Box
									sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}
								>
									<CircularProgress size={14} />
									<Typography variant="caption" color="text.secondary">
										Assistant is thinking…
									</Typography>
								</Box>
							)}

							{session?.status === "FAILED" && session.lastTurnError && (
								<Alert severity="error" sx={{ mt: 1 }}>
									{session.lastTurnError}
								</Alert>
							)}
						</Box>

						{startTurnError && (
							<Alert severity="error" sx={{ mb: 1 }}>
								{startTurnError}
							</Alert>
						)}

						{speech.error && (
							<Alert severity="warning" sx={{ mb: 1 }}>
								Speech recognition error: {speech.error}
							</Alert>
						)}

						<Stack direction="row" spacing={1} alignItems="flex-end">
							<TextField
								fullWidth
								multiline
								maxRows={4}
								size="small"
								inputProps={{ "aria-label": "Message the assistant" }}
								value={
									speech.listening && speech.interim
										? (composer ? composer + " " : "") + speech.interim
										: composer
								}
								onChange={(e) => setComposer(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter" && !e.shiftKey) {
										e.preventDefault();
										handleSend();
									}
								}}
								placeholder={
									session
										? "Reply or describe the next piece…"
										: "e.g. '12-week stimulant program, twice a week, pays on a negative UDT.'"
								}
								disabled={isTurnInFlight}
							/>
							{speech.supported && (
								<Tooltip
									title={
										speech.listening
											? "Listening… say “send message” to send"
											: "Dictate your message"
									}
								>
									<span>
										<IconButton
											aria-label={
												speech.listening ? "Stop dictating" : "Dictate"
											}
											onClick={() =>
												speech.listening ? speech.stop() : speech.start()
											}
											disabled={isTurnInFlight}
											color={speech.listening ? "error" : "default"}
										>
											{speech.listening ? <MicOffIcon /> : <MicIcon />}
										</IconButton>
									</span>
								</Tooltip>
							)}
							<IconButton
								aria-label="Send"
								color="primary"
								onClick={handleSend}
								disabled={isTurnInFlight || !composer.trim()}
							>
								<SendIcon />
							</IconButton>
						</Stack>
					</Paper>
				</Box>

				<Box sx={{ flex: "1 1 35%", minWidth: 280 }}>
					<Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center"
							sx={{ mb: 1 }}
						>
							<Typography variant="subtitle1">Config so far</Typography>
							{session?.status && (
								<Chip
									size="small"
									label={chatStatusLabel[session.status]}
									color={chatStatusColor[session.status]}
								/>
							)}
						</Stack>
						<Divider sx={{ mb: 1 }} />

						<DraftSummary
							draft={draft}
							planOptions={conversion.options}
							labels={labels}
						/>

						{/* Why the button is disabled, stated rather than implied. The
						    gaps come from the same function that builds the template, so
						    they cannot disagree with it. */}
						{draft && conversion.blockers.length > 0 && (
							<Alert severity="warning" sx={{ mt: 2 }}>
								<AlertTitle>Not ready to add</AlertTitle>
								<ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
									{conversion.blockers.map((blocker) => (
										<li key={blocker}>
											<Typography variant="body2">{blocker}</Typography>
										</li>
									))}
								</ul>
							</Alert>
						)}

						{/* Options the assistant would add to the plan, not just the
						    template. Listed because they change what every other template
						    can choose from, which is not obvious from the config above. */}
						{!applied && conversion.additions.length > 0 && (
							<Alert severity="info" sx={{ mt: 2 }}>
								<AlertTitle>Also adds to this plan</AlertTitle>
								<ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
									{conversion.additions.map((addition) => (
										<li key={addition}>
											<Typography variant="body2">{addition}</Typography>
										</li>
									))}
								</ul>
							</Alert>
						)}

						{applied && (
							<Alert severity="success" sx={{ mt: 2 }}>
								Applied. Review it below, then save the plan.
							</Alert>
						)}

						<Button
							fullWidth
							variant="contained"
							startIcon={<PlaylistAddCheckIcon />}
							disabled={!conversion.template}
							onClick={() => {
								if (!conversion.template) return;
								onApply({
									options: conversion.options,
									template: conversion.template,
								});
								setApplied(true);
							}}
							sx={{ mt: 2 }}
						>
							Apply to plan
						</Button>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ display: "block", mt: 1 }}
						>
							Adds the config to the templates below. Options the assistant
							settles on appear there as it goes. Nothing is saved until you
							save the plan.
						</Typography>

						{/* The conversation is not a commitment: whatever has been captured
						    can be finished by hand at any point. */}
						{draft && (
							<Button
								fullWidth
								variant="outlined"
								startIcon={<EditNoteIcon />}
								onClick={() => onContinueManually(draft)}
								sx={{ mt: 1 }}
							>
								Continue manually
							</Button>
						)}
					</Paper>
				</Box>
			</Box>

			{sessionId && (
				<Box sx={{ display: "flex", gap: 2, mt: 2 }}>
					<Button
						variant="outlined"
						color="warning"
						startIcon={<RestartAltIcon />}
						onClick={() => setShowStartOverConfirm(true)}
						disabled={isTurnInFlight}
					>
						Start over
					</Button>
				</Box>
			)}

			{showStartOverConfirm && (
				<GenericAlertModal
					title="Start a new chat?"
					description="Your current conversation will be cleared from this view. Anything already added to the plan stays."
					submitText="Start over"
					cancelText="Cancel"
					onClick={confirmStartOver}
					onCancel={() => setShowStartOverConfirm(false)}
					icon={<RestartAltIcon fontSize="medium" />}
				/>
			)}
		</Box>
	);
};

export default TreatmentConfigChat;
